import { DashboardDate } from '@/features/project-board/dashboard-date';
import { WeeklyTrend } from '@/features/project-board/analytics/trend';
import { ThroughputSeries, type Period } from '@/features/project-board/analytics/throughput';
import { Weekday } from '@/features/project-board/analytics/weekday';
import type { DoneDay, Epic, Issue, Task } from '@/features/project-board/types';

export interface IssueLead {
  average: number;
  median: number;
  longest: number;
  sample: number;
  openedThisMonth: number;
  closedThisMonth: number;
}

export class DashboardPeriod {
  static of(
    tasks: Task[],
    issues: Issue[],
    doneDays: DoneDay[],
    from: string,
    to: string,
    period: Period,
    epics: Epic[] = [],
  ) {
    const fromDate = DashboardDate.fromInput(from);
    const toDate = DashboardDate.fromInput(to);
    if (fromDate === null || toDate === null || fromDate > toDate) throw new Error('올바른 대시보드 조회 기간이 필요하다.');

    const rangeDoneDays = doneDays.filter((day) => DashboardDate.isInRange(day.date, from, to));
    const rangeTasks = tasks.filter((task) => DashboardDate.isInRange(task.updatedAt, from, to));
    const rangeEpics = epics.filter((epic) => DashboardDate.isInRange(epic.updatedAt, from, to));
    const rangeIssues = issues.filter(
      (issue) => DashboardDate.isInRange(issue.reportedAt, from, to) || DashboardDate.isInRange(issue.handledAt, from, to),
    );
    const activityWeeks = DashboardDate.periodCountBetween(fromDate, toDate, 'week');

    return {
      toDate,
      throughput: ThroughputSeries.of(rangeDoneDays, toDate, period, DashboardDate.periodCountBetween(fromDate, toDate, period)),
      weekday: Weekday.statsOf(rangeTasks, toDate),
      activity: WeeklyTrend.activityOf(rangeTasks, rangeIssues, toDate, activityWeeks, rangeEpics),
      issueFlow: WeeklyTrend.issueFlowOf(rangeIssues, toDate, activityWeeks),
      issueLead: DashboardPeriod.issueLeadOf(issues, from, to),
    };
  }

  static issueLeadOf(issues: Issue[], from: string, to: string): IssueLead {
    const month = to.slice(0, 7);
    const days = issues
      .filter(
        (issue) =>
          issue.status === 'done' &&
          issue.reportedAt &&
          issue.handledAt &&
          DashboardDate.isInRange((issue.handledAt as string).slice(0, 10), from, to),
      )
      .map((issue) => {
        const reported = new Date(`${issue.reportedAt}T00:00:00`).getTime();
        const handled = new Date((issue.handledAt as string).slice(0, 10)).getTime();
        return Math.max(0, Math.round((handled - reported) / 86_400_000));
      })
      .sort((left, right) => left - right);

    return {
      average: days.length === 0 ? 0 : Math.round((days.reduce((sum, value) => sum + value, 0) / days.length) * 10) / 10,
      median: days.length === 0 ? 0 : days[Math.floor(days.length / 2)],
      longest: days.at(-1) ?? 0,
      sample: days.length,
      openedThisMonth: issues.filter(
        (issue) => issue.reportedAt.startsWith(month) && DashboardDate.isInRange(issue.reportedAt, from, to),
      ).length,
      closedThisMonth: issues.filter(
        (issue) =>
          issue.status === 'done' &&
          (issue.handledAt ?? '').startsWith(month) &&
          DashboardDate.isInRange((issue.handledAt ?? '').slice(0, 10), from, to),
      ).length,
    };
  }
}
